import InspectionDBStatus from "../../../models/indexedDB/inspection-db-status.entity";
import InspectionStepLocalDB from "../../../models/indexedDB/inspection-step-local-db.entity";
import { SystemFlagsEnum } from "../../../models/session/system-flags.enum";
import IndexedDBService from "../../../services/indexedDB/IndexedDB.service";
import authService from "../../../services/auth.service";
import inspectionEventService from "./inspection-event.service";
import inspectionService from "./inspection.service";
import { classifyUploadError, getRetryAfterSeconds } from "./upload-error.util";

class InspectionUploadService {
  private isDraining = false;

  public callRefreshList() {
    const event = new CustomEvent('refreshList', {});
    document.dispatchEvent(event);
    event.stopPropagation();
  }

  public async recoverStuckUploads() {
    const stuck: any[] = await IndexedDBService
      .getByProperty('status', InspectionDBStatus.SENDING) as any[];

    await Promise.all(stuck.map((item: any) => {
      const record = new InspectionStepLocalDB(item);
      record.status = InspectionDBStatus.WAITING;
      return IndexedDBService.update(record.formatUpdatePayload());
    }));
  }

  public getAllAwaitingPhotos() {
    if (this.isDraining) return;
    if (!authService.isLoginComplete()) return;
    this.isDraining = true;
    this.drainNext();
  }

  private drainNext() {
    if (!authService.isLoginComplete()) {
      this.finishDraining();
      return;
    }

    IndexedDBService
      .getByProperty('status', InspectionDBStatus.WAITING)
      .then((result: any) => {
        const inspectionsGroup: InspectionStepLocalDB[] = result
          .map((item: any) => new InspectionStepLocalDB(item));

        if (!inspectionsGroup.length) {
          this.finishDraining();
          return;
        }

        localStorage.setItem(SystemFlagsEnum.IS_UPLOADING_PHOTO, '1');

        const atualStepToSend: InspectionStepLocalDB = inspectionsGroup[0];

        localStorage
          .setItem(SystemFlagsEnum.SEND_INSPECTION_IDENTIFIER, atualStepToSend.uniqueIdentifier);

        atualStepToSend.status = InspectionDBStatus.SENDING;
        IndexedDBService.update(atualStepToSend.formatUpdatePayload());

        const eventFormData: FormData = inspectionService
          .createdFormDataToInspectEvent(atualStepToSend);

        inspectionEventService.sendFormData(
          '/inspection-event-fleet-offline',
          eventFormData,
          () => {},
        ).then(() => {
          atualStepToSend.status = InspectionDBStatus.SENT;
          IndexedDBService.update(atualStepToSend.formatUpdatePayload());
          this.callRefreshList();
          this.continueOrStop();
        }).catch((error) => {
          this.handleUploadError(atualStepToSend, error);
        });
      })
      .catch(() => {
        this.finishDraining();
      });
  }

  private handleUploadError(step: InspectionStepLocalDB, error: any) {
    switch (classifyUploadError(error)) {
      case 'rate-limited':
        step.status = InspectionDBStatus.WAITING;
        IndexedDBService.update(step.formatUpdatePayload());
        this.finishDraining();
        setTimeout(() => this.getAllAwaitingPhotos(), getRetryAfterSeconds(error) * 1000);
        return;

      case 'unauthorized':
        // Sessão inválida/ausente (ex.: logout): devolve para a fila e aguarda novo login.
        step.status = InspectionDBStatus.WAITING;
        IndexedDBService.update(step.formatUpdatePayload());
        this.finishDraining();
        return;

      case 'already-inspected':
        step.status = InspectionDBStatus.SENT;
        IndexedDBService.update(step.formatUpdatePayload());
        this.callRefreshList();
        this.continueOrStop();
        return;

      case 'vehicle-type':
        step.status = InspectionDBStatus.WITH_ERROR;
        IndexedDBService.update(step.formatUpdatePayload());
        this.callRefreshList();
        this.continueOrStop();
        return;

      case 'validation':
        step.status = InspectionDBStatus.WITH_ERROR;
        IndexedDBService.update(step.formatUpdatePayload());
        this.callRefreshList();
        this.continueOrStop();
        return;

      default:
        step.status = InspectionDBStatus.WAITING;
        IndexedDBService.update(step.formatUpdatePayload());
        this.finishDraining();
    }
  }

  private continueOrStop() {
    if (window.navigator.onLine) {
      this.drainNext();
      return;
    }
    this.finishDraining();
  }

  private finishDraining() {
    this.isDraining = false;
    localStorage.setItem(SystemFlagsEnum.IS_UPLOADING_PHOTO, '0');
    localStorage.removeItem(SystemFlagsEnum.SEND_INSPECTION_IDENTIFIER);
  }

  public checkConnectionAndContinue() {
    this.getAllAwaitingPhotos();
  }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default new InspectionUploadService();
